import { useState, useCallback, useRef } from 'react';

/**
 * Detects if an image is a logo by analyzing its content:
 * 1. Transparency — logos often have transparent backgrounds
 * 2. Color diversity — logos have few flat colors, photos have many
 */
export function useLogoDetection(defaultBgColor = '#f3f4f6') {
  const [isLogo, setIsLogo] = useState(false);
  const [bgColor, setBgColor] = useState(defaultBgColor);

  const handleImageLoad = useCallback(
    (img: HTMLImageElement) => {
      try {
        const sampleSize = 64;
        const canvas = document.createElement('canvas');
        canvas.width = sampleSize;
        canvas.height = sampleSize;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.drawImage(img, 0, 0, sampleSize, sampleSize);
        const data = ctx.getImageData(0, 0, sampleSize, sampleSize).data;
        const totalPixels = sampleSize * sampleSize;

        // 1. Check transparency
        let transparentCount = 0;
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < 200) transparentCount++;
        }
        if (transparentCount > totalPixels * 0.05) {
          setIsLogo(true);
          sampleBgColor(ctx, img, defaultBgColor, setBgColor);
          return;
        }

        // 2. Check for white/near-white background
        let whiteCount = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] > 240 && data[i + 1] > 240 && data[i + 2] > 240) {
            whiteCount++;
          }
        }
        if (whiteCount > totalPixels * 0.3) {
          setIsLogo(true);
          setBgColor('#ffffff');
          return;
        }

        // 3. Check color diversity — bucket colors into ~64 bins
        const colorBuckets = new Set<number>();
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i] >> 4;
          const g = data[i + 1] >> 4;
          const b = data[i + 2] >> 4;
          colorBuckets.add((r << 8) | (g << 4) | b);
        }
        // Photos typically have 500+ unique color buckets, logos have far fewer
        const logoDetected = colorBuckets.size < 150;

        setIsLogo(logoDetected);
        if (logoDetected) {
          sampleBgColor(ctx, img, defaultBgColor, setBgColor);
        }
      } catch {
        // CORS — keep defaults
      }
    },
    [defaultBgColor]
  );

  return { isLogo, bgColor, handleImageLoad };
}

function sampleBgColor(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  defaultBgColor: string,
  setBgColor: (color: string) => void
) {
  try {
    const c = document.createElement('canvas');
    c.width = 1;
    c.height = 1;
    const cx = c.getContext('2d');
    if (cx) {
      cx.drawImage(img, 0, 0, 1, 1, 0, 0, 1, 1);
      const [r, g, b] = cx.getImageData(0, 0, 1, 1).data;
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      setBgColor(brightness < 30 ? '#ffffff' : `rgb(${r}, ${g}, ${b})`);
    }
  } catch {
    // CORS — keep default
  }
}

/**
 * Analyzes a cover image to determine if blur is needed
 * and if the image has a transparent background.
 */
export function useCoverImageAnalysis() {
  const [needsBlur, setNeedsBlur] = useState(true);
  const [hasTransparency, setHasTransparency] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleCoverLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      const container = containerRef.current;
      if (!container) return;

      // Detect transparency by sampling corner pixels
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const corners = [
            [0, 0],
            [img.naturalWidth - 1, 0],
            [0, img.naturalHeight - 1],
            [img.naturalWidth - 1, img.naturalHeight - 1],
          ];
          const transparent = corners.some(([x, y]) => {
            const pixel = ctx.getImageData(x, y, 1, 1).data;
            return pixel[3] < 200;
          });
          if (transparent) {
            setHasTransparency(true);
            setNeedsBlur(false);
            return;
          }
        }
      } catch {
        // CORS — fall through to ratio check
      }

      const containerRatio = container.clientWidth / container.clientHeight;
      const imageRatio = img.naturalWidth / img.naturalHeight;
      const ratioDiff =
        Math.abs(containerRatio - imageRatio) / containerRatio;
      setNeedsBlur(ratioDiff > 0.15);
    },
    []
  );

  const reset = useCallback(() => {
    setNeedsBlur(true);
    setHasTransparency(false);
  }, []);

  return { needsBlur, hasTransparency, containerRef, handleCoverLoad, reset };
}
